import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["header", "input", "subdivisionInput"]
    static values = {
        data: Object,
        initialText: String,
        style: String,
        backText: String,
        defaultValue: String,
        refreshOnSelection: Boolean,
        redirectOnSelection: Boolean,
        refreshKey: String,
        refreshValue: String,
        locked: Boolean
    }

    connect() {
        this.currentCity = null
        this.isOpen = false
        this.firstLoad = true
        this.createDropdownList()
        this.setupOutsideClickListener()
        this.setupPositionListeners()
        this.showCities()

        this.refreshValue = this.validateRefreshValue();

        if (this.hasDefaultValueValue && this.defaultValueValue) {
            this.setDefaultSelection(this.defaultValueValue)
        }

        if (this.hasLockedValue && this.lockedValue) {
            return
        }

        this.headerTarget.addEventListener("click", this.toggleDropdown.bind(this))
    }

    disconnect() {
        this.removeOutsideClickListener()
        this.removePositionListeners()
        this.destroyDropdownList()
    }

    validateRefreshValue() {
        if (this.hasRefreshValueValue) {
            switch (this.refreshValueValue) {
                case 'subdivision_id':
                    return 'subdivision_id';
                case 'club_id':
                    return 'club_id';
                default:
                    throw new Error("Invalid refreshValue. Must be either 'subdivision_id' or 'club_id'.");
            }
        }
    }

    setDefaultSelection(value) {
        for (const [cityName, clubs] of Object.entries(this.dataValue)) {
            for (const club of clubs) {
                if (club[this.refreshValue] && Number(club[this.refreshValue]) === Number(value)) {
                    this.inputTarget.value = club.club_id;
                    this.subdivisionInputTarget.value = club.subdivision_id;
                    this.headerTarget.textContent = club.address;
                    this.currentCity = cityName;
                    return;
                }
            }
        }
    }

    createDropdownList() {
        this.listElement = document.createElement('div')
        this.listElement.className = 'multi-step-dropdown__list'
        this.listElement.style.display = 'none'
        document.body.appendChild(this.listElement)
    }

    destroyDropdownList() {
        if (this.listElement && this.listElement.parentNode) {
            this.listElement.parentNode.removeChild(this.listElement)
            this.listElement = null
        }
    }

    setupOutsideClickListener() {
        this.outsideClickHandler = (event) => {
            if (!this.element.contains(event.target) &&
                this.listElement && !this.listElement.contains(event.target)) {
                this.closeDropdown()
            }
        }
        document.addEventListener("click", this.outsideClickHandler)
    }

    removeOutsideClickListener() {
        if (this.outsideClickHandler) {
            document.removeEventListener("click", this.outsideClickHandler)
        }
    }

    setupPositionListeners() {
        this.positionHandler = this.positionDropdown.bind(this)
        window.addEventListener("scroll", this.positionHandler, true)
        window.addEventListener("resize", this.positionHandler)
    }

    removePositionListeners() {
        if (this.positionHandler) {
            window.removeEventListener("scroll", this.positionHandler, true)
            window.removeEventListener("resize", this.positionHandler)
        }
    }

    positionDropdown() {
        if (!this.isOpen || !this.listElement) return

        const headerRect = this.headerTarget.getBoundingClientRect()

        const top = headerRect.bottom + window.scrollY
        const left = headerRect.left + window.scrollX
        const width = headerRect.width

        this.listElement.style.position = 'absolute'
        this.listElement.style.top = `${top}px`
        this.listElement.style.left = `${left}px`
        this.listElement.style.width = `${width}px`
        this.listElement.style.minWidth = `${width}px`
        this.listElement.style.maxHeight = '200px'
    }

    toggleDropdown(event) {
        event.stopPropagation()

        if (this.isOpen) {
            this.closeDropdown()
        } else {
            this.openDropdown()
        }
    }

    openDropdown() {
        if (!this.listElement) return

        this.element.classList.add("multi-step-dropdown__container--open")
        this.listElement.style.display = 'block'

        // conditional styles for different style types
        switch (this.styleValue) {
            case 'style-2':
                this.headerTarget.classList.add("input-select-alt__active")
                break;
        }

        this.isOpen = true

        this.positionDropdown()

        if (this.currentCity) {
            this.showAddresses(this.currentCity)
        } else {
            this.showCities()
        }

        if (this.firstLoad && this.hasDefaultValueValue) {
            this.showCities()
            this.firstLoad = false;
        }

        this.listElement.scrollTop = 0

        setTimeout(() => this.positionDropdown(), 0)
    }

    closeDropdown() {
        if (!this.listElement) return

        switch (this.styleValue) {
            case 'style-2':
                this.headerTarget.classList.remove("input-select-alt__active")
                break;
        }

        this.element.classList.remove("multi-step-dropdown__container--open")
        this.listElement.style.display = 'none'
        this.isOpen = false
    }

    showCities() {
        if (!this.listElement) return

        this.listElement.innerHTML = ""

        const cities = Object.keys(this.dataValue)

        cities.forEach(city => {
            const item = this.createListItem(city, () => {
                this.selectCity(city)
            })
            this.listElement.appendChild(item)
        })
    }

    showAddresses(city) {
        if (!this.listElement) return

        this.listElement.innerHTML = ""

        const backItem = this.createListItem(this.backTextValue, () => {
            this.goBack()
        }, `multi-step-dropdown__item-${this.styleValue}--back`)
        this.listElement.appendChild(backItem)

        const clubs = this.dataValue[city] || []

        if (this.redirectOnSelectionValue) {
            clubs.forEach(club => {
                const item = this.createListItemLink(club.address, club.url, () => {
                    this.selectAddress(city, club.address, club.club_id, club.subdivision_id)
                })
                this.listElement.appendChild(item)
            })
        } else {
            clubs.forEach(club => {
                const item = this.createListItem(club.address, () => {
                    this.selectAddress(city, club.address, club.club_id, club.subdivision_id)
                })
                this.listElement.appendChild(item)
            })
        }
    }

    createListItem(text, clickHandler, additionalClass = "") {
        const item = document.createElement("div")
        item.className = `multi-step-dropdown__item-${this.styleValue} ${additionalClass}`.trim()
        item.textContent = text
        item.title = text
        item.addEventListener("click", (event) => {
            event.stopPropagation()
            clickHandler()
        })
        return item
    }

    createListItemLink(text, url, clickHandler, additionalClass = "") {
        const item = document.createElement("a")
        item.className = `multi-step-dropdown__item-${this.styleValue} ${additionalClass}`.trim()
        item.textContent = text
        item.title = text
        item.href = url;
        item.addEventListener("click", (event) => {
            event.stopPropagation()
            clickHandler()
        })
        return item
    }

    selectCity(city) {
        this.currentCity = city
        const clubs = this.dataValue[city] || []

        if (clubs.length === 1) {

            if (this.redirectOnSelectionValue) {
                window.location.href = clubs[0].url;
                return;
            }

            this.selectAddress(city, clubs[0].address, clubs[0].club_id, clubs[0].subdivision_id)
        } else {
            this.headerTarget.textContent = `${city}`
            this.showAddresses(city)
        }
    }

    selectAddress(city, address, clubId, subdivisionId) {
        this.headerTarget.textContent = `${address}`
        this.inputTarget.value = clubId
        this.subdivisionInputTarget.value = subdivisionId || ''

        this.closeDropdown()

        if (this.refreshOnSelectionValue && this.refreshValue) {
            this.refreshPageWithSubdivision(this.refreshValue === 'subdivision_id' ? subdivisionId : clubId)
            return
        }

        this.dispatch("selection", {
            detail: {
                city: city,
                address: address,
                clubId: clubId,
                subdivisionId: subdivisionId,
                fullSelection: `${address}`
            }
        })
    }

    refreshPageWithSubdivision(subdivisionId) {
        const url = new URL(window.location)
        url.searchParams.set(this.refreshKeyValue, subdivisionId)
        window.location.href = url.toString()
    }

    goBack() {
        this.currentCity = null
        this.headerTarget.textContent = this.initialTextValue
        this.inputTarget.value = ""
        this.subdivisionInputTarget.value = ""
        this.showCities()
    }
}
